// @ts-check
/**
 * The report's long-text fields carry a constrained markdown subset: bold, italic, bullet lists and
 * paragraph breaks. Anything else the model emits is shown as the text it is, and everything is
 * sanitised before it reaches the DOM.
 *
 * Only asterisk emphasis is honoured. Underscores are left alone because project and metric names in
 * this product contain them (pass_fail, code_smells), and italicising half a name reads as a defect.
 */
import React from 'react';

import { TestableText } from '@components/general/TestableComponents';
import { createSafeMarkup } from '@datahooks/metadata/DataUtils';

const INLINE_TAGS = ['b', 'i'];
const LIST_MARKER = /^[-*]\s+/;

/**
 * A blank line. Horizontal whitespace only: \s would also match the newlines bounding it, and a
 * quantifier that overlaps its own terminator is the shape Sonar reports as super-linear backtracking.
 */
const BLANK_LINE = /\n[ \t\r]*\n/;

/** Bold first, so the inner asterisks of a bold run are not read as italics. */
const toInlineHtml = (text) => text
  .replaceAll(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
  .replaceAll(/\*([^*]+)\*/g, '<i>$1</i>');

/**
 * @param {string} markdown
 * @param {string} keyPrefix - Distinguishes blocks when several fields render into the same parent.
 * @returns {import('react').ReactNode}
 */
export const renderReportMarkdown = (markdown, keyPrefix = 'ai-report-md') => {
  if (typeof markdown !== 'string' || markdown.trim() === '') {
    return null;
  }

  return markdown.trim().split(BLANK_LINE).map((block, blockIndex) => {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    const key = `${keyPrefix}-${blockIndex}`;

    if (lines.length === 0) {
      return null;
    }

    if (lines.every((line) => LIST_MARKER.test(line))) {
      return (
        <ul key={key} className="ml-4 list-disc space-y-1">
          {lines.map((line, lineIndex) => (
            <li key={`${key}-${lineIndex}`}>
              {createSafeMarkup(toInlineHtml(line.replace(LIST_MARKER, '')), INLINE_TAGS)}
            </li>
          ))}
        </ul>
      );
    }

    // A single newline inside a paragraph is wrapping, not a break.
    return (
      <TestableText key={key} variant="small" dataTestId={key}>
        {createSafeMarkup(toInlineHtml(lines.join(' ')), INLINE_TAGS)}
      </TestableText>
    );
  });
};

export default renderReportMarkdown;
